import { validTokenExists } from '../firebaseHelper';
import { getAuthSessionRevision, signinUser, signoutUser } from '../redux/actions/userActions';
import { SET_AUTH_RESTORE_ERROR } from '../redux/types';
import { isTransientRequestFailure, withTransientRetry } from './requestRetry';

const pendingRestorations = new WeakMap();
const invalidCredentials = (error) => {
    if (['auth/no-saved-token', 'auth/invalid-saved-token'].includes(error?.code)) return true;
    if (error?.response?.status === 401) return true;
    const body = error?.response?.data;
    // Token verification also returns 403 when Firebase cannot fetch its keys.
    // Only a definitive credential failure should discard the saved session.
    if (['auth/id-token-revoked', 'auth/invalid-id-token', 'auth/user-disabled', 'auth/user-not-found']
        .includes(body?.code || body?.errorInfo?.code)) return true;
    return /^(INVALID_REFRESH_TOKEN|TOKEN_EXPIRED|USER_DISABLED|USER_NOT_FOUND|INVALID_ID_TOKEN)(\s|$)/
        .test(body?.error?.message || '');
};

export const restoreAuthSession = (dispatch, getState) => {
    const revision = getAuthSessionRevision();
    const pending = pendingRestorations.get(dispatch);
    if (pending?.revision === revision) return pending.promise;
    if (getState().user.authenticated === true) return Promise.resolve(true);
    const sessionRevision = getState().user.forMeSessionRevision;
    const current = () => getAuthSessionRevision() === revision &&
        getState().user.forMeSessionRevision === sessionRevision;
    const request = { revision, attempts: 0 };
    request.promise = (async () => {
        dispatch({ type: SET_AUTH_RESTORE_ERROR, payload: null });
        try {
            await withTransientRetry(async () => {
                request.attempts += 1;
                await validTokenExists();
                if (!current()) return;
                const token = localStorage.getItem('FirebaseIdToken')?.slice('Bearer '.length);
                const refreshToken = localStorage.getItem('FirebaseRefreshToken');
                const result = await dispatch(signinUser(token, refreshToken, {}, { restoreRevision: revision }));
                if (!current() || result?.cancelled) return;
                if (!result?.success) throw result?.error || new Error('Unable to restore your session.');
            }, { shouldContinue: current });
            return current() && getState().user.authenticated === true;
        } catch (error) {
            if (!current() || error?.code === 'ERR_CANCELED') return false;
            request.retryableFailure = isTransientRequestFailure(error);
            if (invalidCredentials(error)) dispatch(signoutUser());
            else dispatch({ type: SET_AUTH_RESTORE_ERROR, payload: 'Unable to restore your session. Please try again.' });
            return false;
        } finally {
            if (pendingRestorations.get(dispatch) === request) pendingRestorations.delete(dispatch);
            if (request.retryOnComplete && request.retryableFailure && current() && getState().user.authRestoreError) {
                restoreAuthSession(dispatch, getState);
            }
        }
    })();
    pendingRestorations.set(dispatch, request);
    return request.promise;
};

export const retryAuthSession = () => (dispatch, getState) => restoreAuthSession(dispatch, getState);

export const installAuthSessionRecovery = (dispatch, getState) => {
    const retry = () => {
        const request = pendingRestorations.get(dispatch);
        if (request?.attempts > 1) request.retryOnComplete = true;
        else if (getState().user.authRestoreError) restoreAuthSession(dispatch, getState);
    };
    window.addEventListener('online', retry);
    restoreAuthSession(dispatch, getState);
    return () => window.removeEventListener('online', retry);
};
